import { Injectable, computed, inject, linkedSignal, resource, signal } from '@angular/core';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { DappaApiService } from '../../shared/services/dappa/dappa-api.service';
import { DappaApi } from '../../shared/interfaces/dappa.interface';

interface DappaApisParams {
  projectId: number;
  versionId: number;
  tick: number;
}

/**
 * Owns the list of Dappa APIs in the active project version (EHQ-339).
 *
 * Follows the projects-list store pattern: the project and version come from
 * {@link AppStateService} (set by the project route guard), a `resource()`
 * refetches whenever they change or a local mutation bumps the tick, and
 * `linkedSignal` keeps the previous rows visible during a refetch.
 */
@Injectable()
export class DappaApisStore {
  readonly #appState = inject(AppStateService);
  readonly #dappa = inject(DappaApiService);

  readonly #tick = signal(0);

  readonly projectId = computed(() => this.#appState.projectId());
  readonly versionId = computed(() => this.#appState.versionId());

  readonly #params = computed<DappaApisParams | undefined>(() => {
    const projectId = this.projectId();
    const versionId = this.versionId();
    // Undefined params leave the resource idle until the guard has loaded both.
    if (!projectId || !versionId) {
      return undefined;
    }
    return { projectId, versionId, tick: this.#tick() };
  });

  readonly #resource = resource<DappaApi[], DappaApisParams | undefined>({
    params: this.#params,
    loader: ({ params }) => this.#dappa.listApis(params.projectId, params.versionId),
  });

  readonly apis = linkedSignal<DappaApi[] | undefined, DappaApi[]>({
    source: () => (this.#resource.hasValue() ? this.#resource.value() : undefined),
    computation: (value, previous) => value ?? previous?.value ?? [],
  });

  readonly isLoading = computed(() => this.#resource.isLoading());
  readonly hasError = computed(() => this.#resource.status() === 'error');

  /** Refetch after a create, update or delete. */
  reload(): void {
    this.#tick.update((t) => t + 1);
  }

  async deleteApi(apiId: number): Promise<void> {
    await this.#dappa.deleteApi(this.projectId(), this.versionId(), apiId);
    this.reload();
  }
}
